/**
 * Coverage AutoFill (CAR) — CAR-5 Flood elevation certificate fill.
 *
 * Maps the shipped `femaFloodProvider` output (`FloodData`) onto the
 * `flood-cert` checklist requirement's `authoredSchema`. The structured
 * fields the user expects (BFE, flood zone, datum, top-of-lowest-floor)
 * are derived ONLY from authoritative public data:
 *   - `floodZone` and `datum` come straight from the FEMA NFHL response.
 *   - `bfe_ft` comes from the NFHL `STATIC_BFE` attribute when present.
 *     When unpublished (Zone A — flood zone known, no BFE published),
 *     we leave it null rather than guess. CAR-FOLLOWUP wires USGS
 *     StreamStats for unpublished-zone BFE estimation.
 *   - `topOfLowestFloor_ft` is derived as `BFE + 2ft` freeboard per
 *     ASCE 24-22 Risk Category III (data center). When BFE is null we
 *     leave it null — never invent a floor elevation without a BFE.
 *
 * Hallucination guard (HARD RULE 8):
 *   - Every non-null structured field carries the FEMA NFHL source URL
 *     in the returned `sourceUrl`.
 *   - The notes string ALWAYS frames the data as auto-filled and
 *     ALWAYS includes the "Commission a real Flood Elevation
 *     Certificate before COL filing" gate.
 *
 * The pack §CAR-5 also calls out FEMA Risk MAP, USGS StreamStats, and
 * NOAA VDatum as data sources. Wiring those is deferred to
 * CAR-FOLLOWUP-FLOOD-EXPAND so this commit ships a working transform on
 * the already-integrated `femaFloodProvider` data — the
 * Phase 1 / CAR-5 scope per pack §Phased rollout (3-day estimate).
 *
 * This module is a PURE transform: it does NOT call any provider, does
 * NOT touch Gemini, and is fully testable with hand-crafted `FloodData`.
 */
import type { FloodData } from '../../services/dataProviders/femaFlood';
import { inferAutoFillConfidence, type InferAutoFillConfidenceResult } from './confidenceTier';

/**
 * ASCE 24-22 Risk Category III freeboard (feet above BFE). Data centres
 * are Category III ("substantial hazard to human life"). State / local
 * jurisdictions may require higher freeboard — flagged in notes.
 */
export const ASCE_24_22_FREEBOARD_FT = 2;

/**
 * `flood-cert` `authoredSchema` field keys, mirrored from
 * `src/config/documentChecklists.ts` (`feasibility[4].authoredSchema`).
 *
 * Pinned here so a checklist-side rename can't silently drift the
 * autofiller from the dialog. Test pins this against the live schema.
 */
export const FLOOD_CERT_FIELD_KEYS = [
  'bfe_ft',
  'floodZone',
  'datum',
  'topOfLowestFloor_ft',
] as const;

/**
 * Structured field values keyed exactly the way the
 * AuthorEvidenceDialog expects on its
 * `AuthoredEvidenceFormState.fields[k]` shape. `notes` is sibling.
 */
export interface FloodCoverageFill {
  /** Per-field values. Unknown fields are omitted (never set to `null`
   *  in the form-state map — the dialog renders the empty input). */
  fields: Partial<Record<typeof FLOOD_CERT_FIELD_KEYS[number], string | number>>;
  /** Free-text notes — always populated with the autofill rationale +
   *  the "Commission a real Flood Elevation Certificate" gate. */
  notes: string;
  /** How many of the 4 structured fields were filled. */
  fieldsFilledCount: number;
  /** Always 4 — the flood-cert schema's structured-field count. */
  fieldsTotalCount: number;
  /** Public FEMA NFHL source URL stamped on every fill. */
  sourceUrl: string;
  /** FEMA-NFHL provenance tag. */
  sourceProvider: 'fema-nfhl';
  /** Confidence band derived from `inferAutoFillConfidence` against
   *  `'provider'` source. */
  confidence: InferAutoFillConfidenceResult;
}

/**
 * Public FEMA NFHL portal — every flood-cert fill cites this URL.
 * (The provider returns this URL on its `DataSource` row too;
 * duplicated here so the transform is self-contained.)
 */
const FEMA_NFHL_URL = 'https://msc.fema.gov/portal/home';

/**
 * Map a `FloodData` payload to the flood-cert coverage shape.
 *
 * @param flood    FEMA NFHL provider response.
 * @param opts     Optional inputs for derived fields:
 *                   - `freeboardFt` overrides ASCE 24-22 default (2ft).
 *                   - `firmPanelId` / `effectiveDate` thread through to
 *                     the notes string when known by the caller (the
 *                     base provider doesn't currently capture them; pack
 *                     wires them in CAR-FOLLOWUP).
 *                   - `riskCategoryLabel` overrides the "Risk Category
 *                     III (data center)" caveat for non-default report
 *                     types.
 */
export function mapFloodDataToCoverage(
  flood: FloodData,
  opts: {
    freeboardFt?: number;
    firmPanelId?: string;
    effectiveDate?: string;
    riskCategoryLabel?: string;
  } = {},
): FloodCoverageFill {
  const freeboardFt = opts.freeboardFt ?? ASCE_24_22_FREEBOARD_FT;
  const riskLabel = opts.riskCategoryLabel ?? 'ASCE 24-22 Risk Category III (data center)';

  const fields: FloodCoverageFill['fields'] = {};
  let filled = 0;

  // ── floodZone ──
  // FEMA returns 'UNMAPPED' when no panel covers the point; we treat
  // that as "not filled" so the user sees the empty input and the
  // dialog can prompt for a real cert.
  const zone = normalizeFloodZone(flood.femaZone);
  if (zone) {
    fields.floodZone = zone;
    filled += 1;
  }

  // ── BFE ──
  // The base provider response (FloodData) does not currently carry a
  // BFE value — the FEMA layer returns `STATIC_BFE` per panel but the
  // shipped provider extracts only `FLD_ZONE` + `SFHA_TF` (femaFlood.ts
  // line 51). For Zone X (low risk) and Zone D (undetermined) there is
  // no BFE by definition. For SFHA zones we leave it null and surface
  // the gap in notes. CAR-FOLLOWUP-FLOOD-BFE adds the STATIC_BFE pull.
  const bfeFt: number | null = null;

  // ── datum ──
  // FEMA NFHL nationally publishes BFEs in NAVD88 since 2009; older
  // panels use NGVD29. Without panel metadata we DON'T guess — leave
  // datum unset and surface the gap.
  // (Conservative: never default-fill datum from imagination.)

  // ── topOfLowestFloor_ft ──
  // Only computable when BFE is known. Otherwise omit.
  if (bfeFt != null) {
    fields.bfe_ft = bfeFt;
    filled += 1;
    fields.topOfLowestFloor_ft = bfeFt + freeboardFt;
    filled += 1;
  }

  // ── notes ──
  const notes = buildFloodNotes({
    flood,
    bfeFt,
    freeboardFt,
    riskLabel,
    firmPanelId: opts.firmPanelId,
    effectiveDate: opts.effectiveDate,
  });

  const confidence = inferAutoFillConfidence({
    fillSource: 'provider',
    fieldsFilledCount: filled,
    fieldsTotalCount: FLOOD_CERT_FIELD_KEYS.length,
  });

  return {
    fields,
    notes,
    fieldsFilledCount: filled,
    fieldsTotalCount: FLOOD_CERT_FIELD_KEYS.length,
    sourceUrl: FEMA_NFHL_URL,
    sourceProvider: 'fema-nfhl',
    confidence,
  };
}

/**
 * Normalize FEMA `FLD_ZONE` values onto the `flood-cert` schema's
 * enum set: `['X', 'A', 'AE', 'AH', 'AO', 'VE', 'V', 'D']`.
 *
 * Returns `null` when the FEMA value isn't recognised — we'd rather
 * leave the field empty than pollute the dialog with a non-enum value
 * that breaks the `<select>`.
 *
 * `X500` (the secondary "moderate flood hazard") is mapped to `'X'`
 * because the flood-cert schema doesn't distinguish them — and ASCE
 * 24-22 treats them the same for freeboard.
 */
export function normalizeFloodZone(zoneRaw: string | undefined | null): 'X' | 'A' | 'AE' | 'AH' | 'AO' | 'VE' | 'V' | 'D' | null {
  if (!zoneRaw || typeof zoneRaw !== 'string') return null;
  const z = zoneRaw.trim().toUpperCase();
  if (z === 'UNMAPPED' || z === '') return null;
  // Direct passthrough for the enum members.
  if (z === 'X' || z === 'A' || z === 'AE' || z === 'AH' || z === 'AO' || z === 'VE' || z === 'V' || z === 'D') {
    return z;
  }
  // Aliases per FEMA documentation.
  if (z === 'X500' || z.includes('MINIMAL') || z === 'C' || z === 'B') return 'X';
  if (z === 'A99') return 'A';
  if (z === 'AR') return 'A';
  return null;
}

/**
 * Build the `notes` string. Always frames the fill as auto-filled,
 * always includes the "real Flood Elevation Certificate required"
 * gate, and surfaces the boundary-sweep detail (sweep size / zone
 * straddle) when present so the report writer can see how the value
 * was determined.
 */
function buildFloodNotes(args: {
  flood: FloodData;
  bfeFt: number | null;
  freeboardFt: number;
  riskLabel: string;
  firmPanelId?: string;
  effectiveDate?: string;
}): string {
  const parts: string[] = [];
  parts.push('Auto-filled from FEMA National Flood Hazard Layer (NFHL).');

  if (args.firmPanelId) {
    const effective = args.effectiveDate ? ` (effective ${args.effectiveDate})` : '';
    parts.push(`FIRM panel ${args.firmPanelId}${effective}.`);
  }

  const zone = args.flood.femaZone;
  if (zone === 'UNMAPPED') {
    parts.push('Site is not currently mapped by FEMA — flood zone is undetermined. Order a Letter of Map Determination (LOMD) or commission a site-specific elevation study.');
  } else {
    const sfha = args.flood.isSpecialFloodHazard ? ' (Special Flood Hazard Area)' : '';
    parts.push(`Site is in Zone ${zone}${sfha}.`);
  }

  const tier = args.flood.areaAnalysis?.geometryTier;
  if (args.flood.zoneOverlap && args.flood.zoneOverlap.length > 1) {
    const overlapStr = args.flood.zoneOverlap.map(z => `${z.zone} ${(z.fraction * 100).toFixed(0)}%`).join(', ');
    const basis = args.flood.analysisMethod === 'polygon-intersection' || args.flood.areaAnalysis?.method === 'polygon-intersection'
      ? 'parcel-polygon intersection'
      : 'boundary sweep';
    parts.push(`Parcel straddles zones (${basis}): ${overlapStr}.`);
  } else if (args.flood.sampledPoints && args.flood.sampledPoints > 1) {
    parts.push(`Determined from ${args.flood.sampledPoints}-point boundary sweep — uniform across parcel.`);
  }
  if (tier) {
    // The geometry-fallback disclosure follows whichever branch was taken.
    parts.push(`Flood geometry was computed with a disclosed ${tier} fallback after an exact-precision clipping failure; it is not exact and complete coverage is not claimed.`);
  }
  const coverageStatus = args.flood.screeningCoverage?.status;
  if (coverageStatus && coverageStatus !== 'complete') {
    parts.push(`FEMA screening coverage is ${coverageStatus}: remaining land is not confirmed flood-free.`);
  }

  if (args.bfeFt != null) {
    parts.push(`BFE published at ${args.bfeFt}ft. Top of lowest floor minimum = BFE + ${args.freeboardFt}ft freeboard per ${args.riskLabel}.`);
  } else if (zone && zone !== 'UNMAPPED' && zone !== 'X' && zone !== 'D') {
    parts.push(`BFE not published on FEMA NFHL for this point — refer to printed FIRM panel for the panel-level BFE, or commission a site-specific elevation study (USGS StreamStats can also estimate the 1% annual chance flood elevation).`);
  }

  parts.push('Local jurisdiction may require higher freeboard — verify with the county / parish floodplain administrator.');
  parts.push('A real Flood Elevation Certificate is required before COL filing — auto-fill is a feasibility-stage estimate only.');

  return parts.join(' ');
}
